import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormLabel,
  LinearProgress,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Sheet,
  Stack,
  Step,
  StepIndicator,
  Stepper,
  Table,
  Typography,
} from '@mui/joy';
import {
  CheckCircle,
  CloudArrowUp as CloudUpload,
  DownloadSimple as Download,
  XCircle as ErrorIcon,
  File as InsertDriveFile,
  Play as PlayArrow,
  ArrowsClockwise as Refresh,
  Flask as Science,
  Warning,
} from '@phosphor-icons/react';

import { apiClient } from '../../lib/api';
import { importsApi } from '../../lib/apiImports';
import type {
  DataImport,
  ImportOptions,
  ImportMatchKey,
  ImportType,
  OverwriteMode,
} from '../../types/imports';
import { useNotification } from '../../contexts/NotificationContext';
import { authService } from '../../lib/auth';

type WizardStep = 0 | 1 | 2 | 3 | 4 | 5; // upload, mapping, options, dry-run, execute, results

interface Props {
  open: boolean;
  onClose: () => void;
  onCompleted?: () => void;
  /**
   * If set, resume an existing DataImport instead of starting from the
   * upload step. The wizard fetches the record and jumps to the step that
   * matches its current status.
   */
  resumeImportId?: number;
}

// Fields the user can map to, grouped by entity. Used to populate the
// per-header Select in the mapping step.
const TARGET_FIELDS: { group: string; fields: { value: string; label: string }[] }[] = [
  {
    group: 'Contact',
    fields: [
      { value: 'contact.first_name', label: 'First Name' },
      { value: 'contact.last_name', label: 'Last Name' },
      { value: 'contact.display_name', label: 'Display Name (auto-split)' },
      { value: 'contact.email', label: 'Email' },
      { value: 'contact.secondary_email', label: 'Secondary Email' },
      { value: 'contact.phone', label: 'Phone' },
      { value: 'contact.secondary_phone', label: 'Secondary Phone' },
      { value: 'contact.company_name', label: 'Company' },
      { value: 'contact.title', label: 'Title' },
      { value: 'contact.website', label: 'Website' },
      { value: 'contact.linkedin_url', label: 'LinkedIn URL' },
      { value: 'contact.facebook_url', label: 'Facebook URL' },
      { value: 'contact.assigned_to', label: 'Assigned To (user)' },
      { value: 'contact.source', label: 'Source' },
      { value: 'contact.tags', label: 'Tags' },
      { value: 'contact.lead_date', label: 'Lead Date' },
      { value: 'contact.qualification_status', label: 'Qualification Status' },
    ],
  },
  {
    group: 'Address',
    fields: [
      { value: 'address.full_address', label: 'Full Address (auto-split → Address 1, City, State, Zip)' },
      { value: 'address.street_address_1', label: 'Street Address 1' },
      { value: 'address.street_address_2', label: 'Street Address 2' },
      { value: 'address.city', label: 'City' },
      { value: 'address.state_province', label: 'State / Province' },
      { value: 'address.postal_code', label: 'Postal Code' },
      { value: 'address.country', label: 'Country' },
    ],
  },
  {
    group: 'Notes',
    fields: [
      { value: 'note.content', label: 'Note Text' },
      { value: 'note.subject', label: 'Note Subject (optional)' },
    ],
  },
  {
    group: 'Opportunity',
    fields: [
      { value: 'opportunity.name', label: 'Opportunity Name' },
      { value: 'opportunity.claim_number', label: 'Claim Number' },
      { value: 'opportunity.policy_number', label: 'Policy Number' },
      { value: 'opportunity.insurance_company', label: 'Insurance Company' },
      { value: 'opportunity.deductible_amount', label: 'Deductible' },
      { value: 'opportunity.date_of_loss', label: 'Date of Loss' },
      { value: 'opportunity.gate_code', label: 'Gate Code' },
      { value: 'opportunity.gated_community', label: 'Gated Community' },

      { value: 'opportunity.description', label: 'Description' },
      { value: 'opportunity.estimated_value', label: 'Estimated Value' },
      { value: 'opportunity.owner', label: 'Owner (user)' },
      { value: 'opportunity.board_name', label: 'Opportunity Board' },
      { value: 'opportunity.board_status_name', label: 'Board Status' },
      { value: 'opportunity.address_1', label: 'Address 1' },
      { value: 'opportunity.address_2', label: 'Address 2' },
      { value: 'opportunity.city', label: 'City' },
      { value: 'opportunity.state', label: 'State' },
      { value: 'opportunity.zip_code', label: 'Zip Code' },
      { value: 'opportunity.tags', label: 'Tags' },
    ],
  },
  {
    group: 'Job',
    fields: [
      { value: 'job.claim_number', label: 'Claim #' },
      { value: 'job.value', label: 'Contract Total (Job Value)' },
      { value: 'job.sales_rep', label: 'Sales Rep (user)' },
      { value: 'job.assigned_to', label: 'Assigned To (user)' },
      { value: 'job.board_name', label: 'Job Board' },
      { value: 'job.board_status_name', label: 'Board Status / Stage' },
      { value: 'job.address_1', label: 'Address 1' },
      { value: 'job.address_2', label: 'Address 2' },
      { value: 'job.city', label: 'City' },
      { value: 'job.state', label: 'State' },
      { value: 'job.zip_code', label: 'Zip Code' },
      { value: 'job.description_free', label: 'Description' },
      { value: 'job.policy_number', label: 'Policy #' },
      { value: 'job.insurance_company', label: 'Insurance Company' },
      { value: 'job.deductible_amount', label: 'Deductible' },
      { value: 'job.date_of_loss', label: 'Date of Loss' },
      { value: 'job.gate_code', label: 'Gate Code' },
      { value: 'job.gated_community', label: 'Gated Community' },
      { value: 'job.po_number', label: 'PO Number (to description)' },
      { value: 'job.external_number', label: 'External Number (to description)' },
      { value: 'job.tags', label: 'Tags' },
    ],
  },
];

// Keep user-facing field names consistent with the mapping menu. The API
// still receives the original paths; labels are presentation only.
const TARGET_LABELS = new Map(TARGET_FIELDS.flatMap(({ group, fields }) =>
  fields.map(({ value, label }) => [value, `${group} — ${label}`] as const),
));

function importFieldLabel(field: string, entity?: string): string {
  const path = field.includes('.') || !entity ? field : `${entity}.${field}`;
  return TARGET_LABELS.get(path) || path.split('.').map((part) =>
    part.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()),
  ).join(' — ');
}

const IMPORT_TYPE_OPTIONS: { value: ImportType; label: string; hint: string }[] = [
  { value: 'contacts', label: 'Contacts', hint: 'People and addresses.' },
  {
    value: 'opportunities',
    label: 'Opportunities',
    hint: 'Contacts + deals on an opportunity board.',
  },
  {
    value: 'jobs',
    label: 'Jobs',
    hint: 'Contacts + jobs on a job board.',
  },
];

function defaultOptionsFor(_type: ImportType): ImportOptions {
  return {
    match_keys: ['email', 'phone'],
    overwrite_mode: 'empty_only',
  };
}

// Which target field paths MUST be mapped for each import type.
// first_name / last_name / display_name satisfy the "have a name" rule (any one).
const REQUIRED_NAME_ONE_OF = [
  'contact.first_name',
  'contact.last_name',
  'contact.display_name',
];

function requiredFieldsFor(type: ImportType): {
  oneOf: string[][]; // groups; each group needs at least one mapping
  allOf: string[]; // every field required
} {
  if (type === 'contacts') {
    return { oneOf: [REQUIRED_NAME_ONE_OF], allOf: [] };
  }
  if (type === 'opportunities') {
    return { oneOf: [REQUIRED_NAME_ONE_OF], allOf: [] };
  }
  // Jobs. The Job's address columns are NOT NULL, but the pipeline mirrors
  // the row's Address onto the Job when only the Address side is mapped, so
  // either half of each pair satisfies the requirement.
  return {
    oneOf: [
      REQUIRED_NAME_ONE_OF,
      ['job.address_1', 'address.street_address_1', 'address.full_address'],
      ['job.city', 'address.city', 'address.full_address'],
      ['job.state', 'address.state_province', 'address.full_address'],
      ['job.zip_code', 'address.postal_code', 'address.full_address'],
    ],
    allOf: [],
  };
}

function mappedTargets(mapping: Record<string, string[]>): Set<string> {
  const s = new Set<string>();
  for (const targets of Object.values(mapping)) {
    for (const t of targets || []) s.add(t);
  }
  return s;
}

function validateMapping(
  type: ImportType,
  mapping: Record<string, string[]>,
): { ok: boolean; missing: string[] } {
  const have = mappedTargets(mapping);
  const req = requiredFieldsFor(type);
  const missing: string[] = [];
  for (const group of req.oneOf) {
    if (!group.some((f) => have.has(f))) {
      missing.push(group.map((field) => importFieldLabel(field)).join(' or '));
    }
  }
  for (const f of req.allOf) {
    if (!have.has(f)) missing.push(importFieldLabel(f));
  }
  return { ok: missing.length === 0, missing };
}

function statusHeaderFor(
  type: ImportType,
  mapping: Record<string, string[]>,
): string | null {
  // Find the CSV column that maps to *.board_status_name — that column's
  // unique values feed the status-mapping UI.
  const target = type === 'jobs' ? 'job.board_status_name' : 'opportunity.board_status_name';
  for (const [header, targets] of Object.entries(mapping)) {
    if ((targets || []).includes(target)) return header;
  }
  return null;
}

function boardHeaderFor(
  type: ImportType,
  mapping: Record<string, string[]>,
): string | null {
  // Same idea as statusHeaderFor, but for per-row board routing.
  const target = type === 'jobs' ? 'job.board_name' : 'opportunity.board_name';
  for (const [header, targets] of Object.entries(mapping)) {
    if ((targets || []).includes(target)) return header;
  }
  return null;
}

function stepForStatus(status: DataImport['status']): WizardStep {
  switch (status) {
    case 'pending':
    case 'analyzing':
      return 1; // mapping step will show a spinner until ready
    case 'ready':
      return 1;
    case 'dry_run':
    case 'dry_run_complete':
      return 3;
    case 'processing':
      return 4;
    case 'completed':
    case 'failed':
    case 'cancelled':
    case 'reversed':
      return 5;
    default:
      return 1;
  }
}

export const DataImportWizard: React.FC<Props> = ({
  open,
  onClose,
  onCompleted,
  resumeImportId,
}) => {
  const { showError, showSuccess } = useNotification();

  const [step, setStep] = useState<WizardStep>(0);
  const [importType, setImportType] = useState<ImportType>('jobs');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const [dataImport, setDataImport] = useState<DataImport | null>(null);
  const [mapping, setMapping] = useState<Record<string, string[]>>({});
  const [options, setOptions] = useState<ImportOptions>(defaultOptionsFor('jobs'));

  const pollRef = useRef<number | null>(null);
  const wsRef = useRef<WebSocket | null>(null);

  const reset = () => {
    setStep(0);
    setImportType('jobs');
    setFile(null);
    setUploading(false);
    setDataImport(null);
    setMapping({});
    setOptions(defaultOptionsFor('jobs'));
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  };

  useEffect(() => () => reset(), []);

  // If the wizard is opened with a resumeImportId, hydrate from that record.
  useEffect(() => {
    if (!open || !resumeImportId) return;
    let cancelled = false;
    (async () => {
      try {
        const existing = await importsApi.getImport(resumeImportId);
        if (cancelled) return;
        setDataImport(existing);
        setImportType(existing.import_type);
        setMapping(existing.column_mapping || {});
        setOptions(
          existing.options && Object.keys(existing.options).length
            ? (existing.options as ImportOptions)
            : defaultOptionsFor(existing.import_type),
        );
        setStep(stepForStatus(existing.status));
      } catch (err) {
        showError(err instanceof Error ? err.message : 'Failed to load import');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resumeImportId]);

  // Auto-refresh only while the server is actively working
  // (analyzing / dry-running / processing). Once the server returns a
  // "finished-for-now" status we stop polling to avoid clobbering user edits
  // such as the column mapping.
  useEffect(() => {
    const busy =
      dataImport &&
      (dataImport.status === 'pending' ||
        dataImport.status === 'analyzing' ||
        dataImport.status === 'dry_run' ||
        dataImport.status === 'processing');

    if (!busy) {
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    if (pollRef.current) return;
    pollRef.current = window.setInterval(async () => {
      try {
        const latest = await importsApi.getImport(dataImport!.id);
        setDataImport(latest);
      } catch {
        // swallow; will retry
      }
    }, 2500);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [dataImport?.id, dataImport?.status]);

  // Open a WebSocket when the import starts processing
  useEffect(() => {
    if (!dataImport) return;
    if (dataImport.status !== 'processing' && dataImport.status !== 'dry_run') return;
    if (wsRef.current) return;

    const token = authService.getAccessToken();
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const url = `${proto}://${window.location.host}/ws/imports/${dataImport.id}/`;
    const subprotocols = token ? ['access_token', token] : undefined;
    try {
      const ws = subprotocols ? new WebSocket(url, subprotocols) : new WebSocket(url);
      wsRef.current = ws;
      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === 'progress' && msg.payload) {
            setDataImport((prev) => (prev ? { ...prev, ...msg.payload } : prev));
            if (msg.payload.final) {
              ws.close();
              wsRef.current = null;
              // Final WS payload carries counters but not dry_run_summary.
              // Refetch the full record so the UI gets the sample issues
              // and the complete summary block.
              const importId = msg.payload.id;
              if (importId) {
                importsApi
                  .getImport(importId)
                  .then((latest) => setDataImport(latest))
                  .catch(() => {
                    /* swallow — polling or manual refresh will recover */
                  });
              }
            }
          }
        } catch {
          /* ignore */
        }
      };
      ws.onclose = () => {
        wsRef.current = null;
      };
    } catch {
      // WebSocket optional — polling covers us.
    }
  }, [dataImport?.status]);

  const handleFileSelected = (f: File) => {
    setFile(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const created = await importsApi.createImport({
        importType,
        file,
        options: defaultOptionsFor(importType),
      });
      setDataImport(created);
      setOptions(created.options && Object.keys(created.options).length
        ? (created.options as ImportOptions)
        : defaultOptionsFor(importType),
      );
      setStep(1);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveMapping = async () => {
    if (!dataImport) return;
    const check = validateMapping(importType, mapping);
    if (!check.ok) {
      showError(`Please map these required fields: ${check.missing.join('; ')}`);
      return;
    }
    try {
      const updated = await importsApi.updateImport(dataImport.id, {
        column_mapping: mapping,
        options,
      });
      setDataImport(updated);
      setStep(2);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save mapping');
    }
  };

  const handleSaveOptions = async () => {
    if (!dataImport) return;
    try {
      const updated = await importsApi.updateImport(dataImport.id, {
        column_mapping: mapping,
        options,
      });
      setDataImport(updated);
      setStep(3);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save options');
    }
  };

  const handleDryRun = async () => {
    if (!dataImport) return;
    try {
      const started = await importsApi.dryRunImport(dataImport.id);
      setDataImport(started);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Dry run failed to start');
    }
  };

  const handleExecute = async () => {
    if (!dataImport) return;
    try {
      const started = await importsApi.executeImport(dataImport.id);
      setDataImport(started);
      setStep(4);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Execute failed to start');
    }
  };

  const handleFinish = () => {
    showSuccess('Import finished');
    onCompleted?.();
    reset();
    onClose();
  };

  const analyzing = dataImport?.status === 'analyzing' || dataImport?.status === 'pending';
  const readyToMap =
    dataImport &&
    (dataImport.status === 'ready' || dataImport.status === 'dry_run_complete' || dataImport.status === 'completed');

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        size="lg"
        sx={{ width: 'min(1100px, 96vw)', maxHeight: '92vh', overflow: 'auto' }}
      >
        <ModalClose />
        <Typography level="h3" sx={{ mb: 1 }}>
          Import Data
        </Typography>
        <Typography level="body-sm" sx={{ mb: 2, color: 'neutral.500' }}>
          Upload a CSV, map columns, preview a dry run, then import.
        </Typography>

        <Stepper sx={{ mb: 3 }}>
          {['Upload', 'Map Columns', 'Options', 'Dry Run', 'Import', 'Done'].map((label, idx) => (
            <Step
              key={label}
              indicator={
                <StepIndicator
                  variant={step === idx ? 'solid' : step > idx ? 'soft' : 'outlined'}
                  color={step >= idx ? 'primary' : 'neutral'}
                >
                  {step > idx ? <CheckCircle size={18} /> : idx + 1}
                </StepIndicator>
              }
            >
              {label}
            </Step>
          ))}
        </Stepper>

        {/* Step 0: Type + Upload */}
        {step === 0 && (
          <UploadStep
            importType={importType}
            setImportType={(t) => {
              setImportType(t);
              setOptions(defaultOptionsFor(t));
            }}
            file={file}
            onFile={handleFileSelected}
            onUpload={handleUpload}
            uploading={uploading}
          />
        )}

        {/* Step 1: Mapping */}
        {step === 1 && dataImport && (
          <MappingStep
            dataImport={dataImport}
            importType={importType}
            analyzing={!!analyzing || !readyToMap}
            mapping={mapping}
            setMapping={setMapping}
            onBack={() => setStep(0)}
            onNext={handleSaveMapping}
          />
        )}

        {/* Step 2: Options */}
        {step === 2 && dataImport && (
          <OptionsStep
            importType={dataImport.import_type}
            dataImport={dataImport}
            mapping={mapping}
            options={options}
            setOptions={setOptions}
            onBack={() => setStep(1)}
            onNext={handleSaveOptions}
          />
        )}

        {/* Step 3: Dry Run */}
        {step === 3 && dataImport && (
          <DryRunStep
            dataImport={dataImport}
            onRun={handleDryRun}
            onBack={() => setStep(2)}
            onExecute={handleExecute}
          />
        )}

        {/* Step 4: Executing */}
        {step === 4 && dataImport && (
          <ProgressStep dataImport={dataImport} onNext={() => setStep(5)} />
        )}

        {/* Step 5: Results */}
        {step === 5 && dataImport && (
          <ResultsStep dataImport={dataImport} onDone={handleFinish} />
        )}
      </ModalDialog>
    </Modal>
  );
};

// --------------------------------------------------------------------------
// Step 0: Upload
// --------------------------------------------------------------------------
const UploadStep: React.FC<{
  importType: ImportType;
  setImportType: (t: ImportType) => void;
  file: File | null;
  onFile: (f: File) => void;
  onUpload: () => void;
  uploading: boolean;
}> = ({ importType, setImportType, file, onFile, onUpload, uploading }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  return (
    <Stack spacing={3}>
      <FormControl>
        <FormLabel>What are you importing?</FormLabel>
        <Select
          value={importType}
          onChange={(_, v) => v && setImportType(v as ImportType)}
        >
          {IMPORT_TYPE_OPTIONS.map((opt) => (
            <Option key={opt.value} value={opt.value}>
              {opt.label} — {opt.hint}
            </Option>
          ))}
        </Select>
      </FormControl>

      <Sheet
        variant="outlined"
        sx={{
          p: 4,
          borderRadius: 'md',
          borderStyle: 'dashed',
          borderColor: dragOver ? 'primary.500' : 'neutral.300',
          textAlign: 'center',
          backgroundColor: dragOver ? 'primary.50' : 'background.surface',
          cursor: 'pointer',
        }}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const f = e.dataTransfer.files?.[0];
          if (f) onFile(f);
        }}
      >
        <CloudUpload size={44} color="var(--joy-palette-primary-500)" />
        <Typography level="body-md" sx={{ mt: 1 }}>
          {file ? file.name : 'Drop a CSV here or click to browse'}
        </Typography>
        <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
          Max 50 MB. CSV only.
        </Typography>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onFile(f);
          }}
        />
      </Sheet>

      <Stack direction="row" justifyContent="flex-end">
        <Button
          disabled={!file || uploading}
          onClick={onUpload}
          startDecorator={uploading ? <CircularProgress size="sm" /> : <CloudUpload />}
        >
          {uploading ? 'Uploading…' : 'Upload & Analyze'}
        </Button>
      </Stack>
    </Stack>
  );
};

// --------------------------------------------------------------------------
// Step 1: Mapping
// --------------------------------------------------------------------------
const MappingStep: React.FC<{
  dataImport: DataImport;
  importType: ImportType;
  analyzing: boolean;
  mapping: Record<string, string[]>;
  setMapping: (m: Record<string, string[]>) => void;
  onBack: () => void;
  onNext: () => void;
}> = ({ dataImport, importType, analyzing, mapping, setMapping, onBack, onNext }) => {
  // Seed mapping from the server's suggested mapping once analysis completes
  useEffect(() => {
    if (!analyzing && Object.keys(mapping).length === 0) {
      setMapping(dataImport.column_mapping || {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyzing, dataImport.id]);

  if (analyzing) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Analyzing your file…</Typography>
      </Box>
    );
  }

  const headers = dataImport.detected_headers || [];
  const samples = dataImport.sample_rows || [];

  // Only surface the entity groups relevant to the chosen import type so
  // a "Jobs" import doesn't accidentally map columns to Opportunity fields
  // (and vice-versa). Contact + Address are always relevant.
  const visibleTargetGroups = TARGET_FIELDS.filter((group) => {
    if (group.group === 'Contact' || group.group === 'Address' || group.group === 'Notes') return true;
    if (group.group === 'Job') return importType === 'jobs';
    if (group.group === 'Opportunity') return importType === 'opportunities';
    return true;
  });

  const visibleTargetValues = new Set(
    visibleTargetGroups.flatMap((group) => group.fields.map((f) => f.value)),
  );

  const updateHeader = (header: string, targets: string[]) => {
    // A preset can map a column to an entity this import type doesn't show
    // (jobnimbus points its address column at both job and opportunity).
    // Those targets can't be in the Select's value, so carry them through
    // rather than letting a click on the row destroy them.
    const hidden = (mapping[header] || []).filter((t) => !visibleTargetValues.has(t));
    const merged = [...(targets || []), ...hidden];
    const next = { ...mapping };
    if (merged.length === 0) {
      delete next[header];
    } else {
      next[header] = merged;
    }
    setMapping(next);
  };

  const hasFullAddressMapped = Object.values(mapping).some((targets) =>
    (targets || []).includes('address.full_address'),
  );

  return (
    <Stack spacing={2}>
      <Alert color="primary" variant="soft">
        Detected preset: <strong>{dataImport.preset || 'none'}</strong> · {headers.length} columns · {dataImport.total_rows} rows
      </Alert>
      <Typography level="body-sm">
        Map multiple columns to Note Text to combine their nonblank values into one contact note per row,
        labeled with the CSV column names. Map each other destination field to one column.
        {importType === 'jobs' && ' Contract Total (Job Value) is the amount shown as Contract Total in job financials.'}
      </Typography>

      {hasFullAddressMapped && (
        <Alert color="warning" variant="soft">
          <Stack spacing={0.5}>
            <Typography level="title-sm">Full Address auto-split</Typography>
            <Typography level="body-sm">
              The mapped column will be split into <strong>Street Address 1</strong>,{' '}
              <strong>City</strong>, <strong>State</strong>, and <strong>Zip</strong>{' '}
              by separating on commas. Expected format:{' '}
              <code>123 Main St, Springfield, IL, 62701</code> (3-part{' '}
              <code>123 Main St, Springfield, IL 62701</code> also works).
            </Typography>
            <Typography level="body-xs" sx={{ color: 'neutral.600' }}>
              If you also map any of those fields explicitly, the explicit value wins.
              Street Address 2 and Country are not auto-filled.
            </Typography>
          </Stack>
        </Alert>
      )}

      <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto', maxHeight: 460 }}>
        <Table stickyHeader size="sm">
          <thead>
            <tr>
              <th style={{ width: '25%' }}>CSV Column</th>
              <th style={{ width: '35%' }}>Sample</th>
              <th style={{ width: '40%' }}>Maps To</th>
            </tr>
          </thead>
          <tbody>
            {headers.map((h) => {
              const columnSamples = samples
                .map((row) => (row?.[h] || '').trim())
                .filter(Boolean)
                .slice(0, 3);
              // Hidden-group targets stay in the mapping but can't be shown
              // in the Select — updateHeader merges them back on change.
              const selected = (mapping[h] || []).filter((t) => visibleTargetValues.has(t));
              return (
                <tr key={h}>
                  <td>
                    <Typography level="body-sm" fontWeight="lg">
                      {h}
                    </Typography>
                  </td>
                  <td>
                    {columnSamples.length === 0 ? (
                      <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                        <em>empty</em>
                      </Typography>
                    ) : (
                      <Stack spacing={0.25}>
                        {columnSamples.map((s, i) => (
                          <Typography
                            key={i}
                            level="body-xs"
                            sx={{
                              color: 'neutral.500',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: 320,
                            }}
                            title={s}
                          >
                            {s}
                          </Typography>
                        ))}
                      </Stack>
                    )}
                  </td>
                  <td>
                    {/* Multi-select: one column often feeds several entities
                        (street address → Address + Job), which the pipeline
                        has always supported. */}
                    <Select
                      multiple
                      value={selected}
                      onChange={(_, v) => updateHeader(h, (v as string[]) || [])}
                      placeholder="(not mapped)"
                      size="sm"
                      renderValue={(opts) => (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {opts.map((o) => (
                            <Chip key={o.value} size="sm" variant="soft" color="primary">
                              {importFieldLabel(String(o.value))}
                            </Chip>
                          ))}
                        </Stack>
                      )}
                      sx={{ width: '100%' }}
                    >
                      {visibleTargetGroups.flatMap((group) => [
                        <Option
                          key={`__${group.group}`}
                          value={`__${group.group}`}
                          disabled
                        >
                          — {group.group} —
                        </Option>,
                        ...group.fields.map((f) => (
                          <Option key={f.value} value={f.value}>
                            {importFieldLabel(f.value)}
                          </Option>
                        )),
                      ])}
                    </Select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Sheet>

      {(() => {
        const check = validateMapping(importType, mapping);
        return (
          <>
            {!check.ok && (
              <Alert color="warning" variant="soft" startDecorator={<Warning />}>
                <Box>
                  <Typography level="title-sm">Required Fields Not Mapped Yet</Typography>
                  <Typography level="body-sm">Map at least one field from each line below:</Typography>
                  <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5, '& li + li': { mt: 0.75 } }}>
                    {check.missing.map((requirement) => <li key={requirement}>{requirement}</li>)}
                  </Box>
                </Box>
              </Alert>
            )}
            <Stack direction="row" justifyContent="space-between">
              <Button variant="plain" onClick={onBack}>
                Back
              </Button>
              <Button onClick={onNext} disabled={!check.ok}>
                Next: Options
              </Button>
            </Stack>
          </>
        );
      })()}
    </Stack>
  );
};

// --------------------------------------------------------------------------
// Step 2: Options (match rules + board + per-status mapping)
// --------------------------------------------------------------------------

interface BoardLike {
  id: number;
  name: string;
  color?: string;
}

interface BoardStatusLike {
  id: number;
  name: string;
  order: number | null;
  board: number;
}

interface ContactSourceLike {
  id: number;
  name: string;
}

interface OrgUserLike {
  id: number;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  email: string;
}

const userLabel = (u: OrgUserLike): string =>
  u.full_name?.trim() || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;

const OptionsStep: React.FC<{
  importType: ImportType;
  dataImport: DataImport;
  mapping: Record<string, string[]>;
  options: ImportOptions;
  setOptions: (o: ImportOptions) => void;
  onBack: () => void;
  onNext: () => void;
}> = ({ importType, dataImport, mapping, options, setOptions, onBack, onNext }) => {
  const { showError } = useNotification();
  const needsBoard = importType === 'jobs' || importType === 'opportunities';
  const statusHeader = statusHeaderFor(importType, mapping);
  const boardHeader = boardHeaderFor(importType, mapping);

  const [boards, setBoards] = useState<BoardLike[] | null>(null);
  const [statuses, setStatuses] = useState<BoardStatusLike[]>([]);
  const [csvStatuses, setCsvStatuses] = useState<
    { value: string; count: number }[] | null
  >(null);
  const [csvBoards, setCsvBoards] = useState<
    { value: string; count: number }[] | null
  >(null);
  const [loadingStatuses, setLoadingStatuses] = useState(false);
  const [loadingBoardValues, setLoadingBoardValues] = useState(false);
  const [sources, setSources] = useState<ContactSourceLike[]>([]);
  const [users, setUsers] = useState<OrgUserLike[]>([]);

  // Run-wide contact defaults. Both lists are small and org-scoped.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [sourceResp, userResp] = await Promise.all([
          apiClient.get<ContactSourceLike[] | { results: ContactSourceLike[] }>(
            '/api/crm/contact-sources/',
          ),
          apiClient.getUsers(),
        ]);
        if (cancelled) return;
        const sourceData = sourceResp.data;
        setSources(Array.isArray(sourceData) ? sourceData : sourceData.results);
        setUsers(userResp.data);
      } catch {
        // Non-fatal: the defaults are optional, so leave the pickers empty.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load boards once per import-type
  useEffect(() => {
    if (!needsBoard) {
      setBoards([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const list =
          importType === 'jobs'
            ? await importsApi.listJobBoards()
            : await importsApi.listOpportunityBoards();
        if (!cancelled) setBoards(list);
      } catch (err) {
        if (!cancelled) {
          showError(err instanceof Error ? err.message : 'Failed to load boards');
          setBoards([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importType]);

  // When a board is picked, load its statuses
  useEffect(() => {
    if (!needsBoard || !options.board_id) {
      setStatuses([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const list =
          importType === 'jobs'
            ? await importsApi.listJobBoardStatuses(options.board_id!)
            : await importsApi.listOpportunityBoardStatuses(options.board_id!);
        if (!cancelled) setStatuses(list);
      } catch (err) {
        if (!cancelled) {
          showError(err instanceof Error ? err.message : 'Failed to load statuses');
          setStatuses([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importType, options.board_id]);

  // Pull unique CSV status values once the user has mapped a status column
  useEffect(() => {
    if (!needsBoard || !statusHeader) {
      setCsvStatuses(null);
      return;
    }
    let cancelled = false;
    setLoadingStatuses(true);
    (async () => {
      try {
        const resp = await importsApi.getColumnValues(dataImport.id, statusHeader);
        if (!cancelled) setCsvStatuses(resp.values);
      } catch (err) {
        if (!cancelled) {
          showError(err instanceof Error ? err.message : 'Failed to fetch column values');
          setCsvStatuses([]);
        }
      } finally {
        if (!cancelled) setLoadingStatuses(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusHeader, dataImport.id]);

  // Pull unique CSV board-column values when mapped
  useEffect(() => {
    if (!needsBoard || !boardHeader) {
      setCsvBoards(null);
      return;
    }
    let cancelled = false;
    setLoadingBoardValues(true);
    (async () => {
      try {
        const resp = await importsApi.getColumnValues(dataImport.id, boardHeader);
        if (!cancelled) setCsvBoards(resp.values);
      } catch (err) {
        if (!cancelled) {
          showError(err instanceof Error ? err.message : 'Failed to fetch board column values');
          setCsvBoards([]);
        }
      } finally {
        if (!cancelled) setLoadingBoardValues(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardHeader, dataImport.id]);

  // Auto-map CSV board values to ApexOS boards by name
  useEffect(() => {
    if (!csvBoards || !boards || boards.length === 0) return;
    const currentMap = options.board_map || {};
    const nextMap: Record<string, number> = { ...currentMap };
    let changed = false;
    for (const { value } of csvBoards) {
      if (nextMap[value]) continue;
      const match = boards.find(
        (b) => b.name.trim().toLowerCase() === value.trim().toLowerCase(),
      );
      if (match) {
        nextMap[value] = match.id;
        changed = true;
      }
    }
    if (changed) setOptions({ ...options, board_map: nextMap });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csvBoards, boards]);

  // Auto-map CSV statuses to ApexOS statuses on exact (case-insensitive) match
  useEffect(() => {
    if (!csvStatuses || statuses.length === 0) return;
    const currentMap = options.status_map || {};
    const nextMap: Record<string, number> = { ...currentMap };
    let changed = false;
    for (const { value } of csvStatuses) {
      if (nextMap[value]) continue;
      const match = statuses.find(
        (s) => s.name.trim().toLowerCase() === value.trim().toLowerCase(),
      );
      if (match) {
        nextMap[value] = match.id;
        changed = true;
      }
    }
    if (changed) setOptions({ ...options, status_map: nextMap });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csvStatuses, statuses]);

  const toggleMatchKey = (key: ImportMatchKey) => {
    const current = options.match_keys || [];
    setOptions({
      ...options,
      match_keys: current.includes(key)
        ? current.filter((k) => k !== key)
        : [...current, key],
    });
  };

  const setStatusMap = (csvValue: string, targetId: number | null) => {
    const nextMap = { ...(options.status_map || {}) };
    if (targetId == null) {
      delete nextMap[csvValue];
    } else {
      nextMap[csvValue] = targetId;
    }
    setOptions({ ...options, status_map: nextMap });
  };

  const setBoardMap = (csvValue: string, targetId: number | null) => {
    const nextMap = { ...(options.board_map || {}) };
    if (targetId == null) {
      delete nextMap[csvValue];
    } else {
      nextMap[csvValue] = targetId;
    }
    setOptions({ ...options, board_map: nextMap });
  };

  // Either a default board is chosen, OR every distinct CSV board value is
  // explicitly mapped. Default board is still useful as a fallback even when
  // board_map is set.
  const allCsvBoardsMapped =
    !!boardHeader &&
    !!csvBoards &&
    csvBoards.length > 0 &&
    csvBoards.every((cb) => !!(options.board_map || {})[cb.value]);
  const canProceed =
    !needsBoard || !!options.board_id || allCsvBoardsMapped;

  return (
    <Stack spacing={3}>
      {/* Match rules */}
      <FormControl>
        <FormLabel>Match existing records by</FormLabel>
        <Stack direction="row" spacing={1}>
          {(['email', 'phone', 'name_zip'] as ImportMatchKey[]).map((k) => {
            const active = (options.match_keys || []).includes(k);
            return (
              <Chip
                key={k}
                variant={active ? 'solid' : 'outlined'}
                color={active ? 'primary' : 'neutral'}
                onClick={() => toggleMatchKey(k)}
              >
                {k === 'name_zip' ? 'Name + Zip' : k[0].toUpperCase() + k.slice(1)}
              </Chip>
            );
          })}
        </Stack>
        <Typography level="body-xs" sx={{ mt: 0.5, color: 'neutral.500' }}>
          Applied in order. First match wins.
        </Typography>
      </FormControl>

      <FormControl>
        <FormLabel>When a match is found</FormLabel>
        <Select
          value={options.overwrite_mode || 'empty_only'}
          onChange={(_, v) =>
            v && setOptions({ ...options, overwrite_mode: v as OverwriteMode })
          }
        >
          <Option value="empty_only">Update empty fields only (safest)</Option>
          <Option value="always">Overwrite with CSV value</Option>
          <Option value="never">Never update — only create new records</Option>
        </Select>
      </FormControl>

      <Divider />

      <FormControl>
        <FormLabel>Default lead source</FormLabel>
        <Select
          placeholder="(none)"
          value={options.default_source_id != null ? String(options.default_source_id) : ''}
          onChange={(_, v) =>
            setOptions({ ...options, default_source_id: v ? Number(v) : undefined })
          }
        >
          <Option value="">(none)</Option>
          {sources.map((s) => (
            <Option key={s.id} value={String(s.id)}>
              {s.name}
            </Option>
          ))}
        </Select>
        <Typography level="body-xs" sx={{ mt: 0.5, color: 'neutral.500' }}>
          Applied to contacts whose row has no Source column value.
        </Typography>
      </FormControl>

      <FormControl>
        <FormLabel>Default assignee</FormLabel>
        <Select
          placeholder="(none)"
          value={options.default_assigned_to_id != null ? String(options.default_assigned_to_id) : ''}
          onChange={(_, v) =>
            setOptions({ ...options, default_assigned_to_id: v ? Number(v) : undefined })
          }
        >
          <Option value="">(none)</Option>
          {users.map((u) => (
            <Option key={u.id} value={String(u.id)}>
              {userLabel(u)}
            </Option>
          ))}
        </Select>
        <Typography level="body-xs" sx={{ mt: 0.5, color: 'neutral.500' }}>
          Applied to contacts whose row has no Assigned To column value.
        </Typography>
      </FormControl>

      {/* Board picker + status mapping (jobs / opportunities only) */}
      {needsBoard && (
        <>
          <Divider />
          <FormControl>
            <FormLabel>
              Default {importType === 'jobs' ? 'job board' : 'opportunity board'}
            </FormLabel>
            {boards === null ? (
              <Typography level="body-sm">Loading boards…</Typography>
            ) : boards.length === 0 ? (
              <Alert color="danger" variant="soft">
                No {importType === 'jobs' ? 'job' : 'opportunity'} boards found. Create one first.
              </Alert>
            ) : (
              <Select
                placeholder="Pick a board"
                value={options.board_id ?? null}
                onChange={(_, v) =>
                  setOptions({
                    ...options,
                    board_id: typeof v === 'number' ? v : undefined,
                    status_map: {},
                  })
                }
              >
                {boards.map((b) => (
                  <Option key={b.id} value={b.id}>
                    {b.name}
                  </Option>
                ))}
              </Select>
            )}
            <Typography level="body-xs" sx={{ mt: 0.5, color: 'neutral.500' }}>
              Used for every row unless overridden by the board mapping below.
            </Typography>
          </FormControl>

          {boardHeader && boards && boards.length > 0 && (
            <FormControl>
              <FormLabel>Board routing</FormLabel>
              {loadingBoardValues ? (
                <Typography level="body-sm">Reading board values from CSV…</Typography>
              ) : csvBoards && csvBoards.length === 0 ? (
                <Alert color="neutral" variant="soft">
                  The "{boardHeader}" column is empty in every row.
                </Alert>
              ) : (
                <Sheet
                  variant="outlined"
                  sx={{ borderRadius: 'md', overflow: 'auto', maxHeight: 260 }}
                >
                  <Table stickyHeader size="sm">
                    <thead>
                      <tr>
                        <th style={{ width: '45%' }}>CSV value (in "{boardHeader}")</th>
                        <th style={{ width: '15%' }}>Rows</th>
                        <th style={{ width: '40%' }}>Import into</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(csvBoards || []).map((cb) => {
                        const mappedTo = options.board_map?.[cb.value] ?? null;
                        return (
                          <tr key={cb.value}>
                            <td>{cb.value}</td>
                            <td>{cb.count}</td>
                            <td>
                              <Select
                                size="sm"
                                value={mappedTo != null ? String(mappedTo) : ''}
                                onChange={(_, v) =>
                                  setBoardMap(
                                    cb.value,
                                    v ? Number(v) : null,
                                  )
                                }
                                placeholder="(use default board)"
                              >
                                <Option value="">(use default board)</Option>
                                {boards.map((b) => (
                                  <Option key={b.id} value={String(b.id)}>
                                    {b.name}
                                  </Option>
                                ))}
                              </Select>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </Sheet>
              )}
            </FormControl>
          )}

          {options.board_id && (
            <FormControl>
              <FormLabel>Status mapping</FormLabel>
              {!statusHeader ? (
                <Alert color="warning" variant="soft">
                  No CSV column is mapped to{' '}
                  <strong>
                    {importType === 'jobs'
                      ? 'Job → Board Status / Stage'
                      : 'Opportunity → Board Status'}
                  </strong>
                  . Go back and map one, or every row will use the board's default status.
                </Alert>
              ) : loadingStatuses ? (
                <Typography level="body-sm">Reading status values from CSV…</Typography>
              ) : csvStatuses && csvStatuses.length === 0 ? (
                <Alert color="neutral" variant="soft">
                  The "{statusHeader}" column is empty in every row.
                </Alert>
              ) : (
                <Sheet
                  variant="outlined"
                  sx={{ borderRadius: 'md', overflow: 'auto', maxHeight: 320 }}
                >
                  <Table stickyHeader size="sm">
                    <thead>
                      <tr>
                        <th style={{ width: '45%' }}>CSV value (in "{statusHeader}")</th>
                        <th style={{ width: '15%' }}>Rows</th>
                        <th style={{ width: '40%' }}>Maps to</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(csvStatuses || []).map((cs) => {
                        const mappedTo = options.status_map?.[cs.value] ?? null;
                        return (
                          <tr key={cs.value}>
                            <td>{cs.value}</td>
                            <td>{cs.count}</td>
                            <td>
                              <Select
                                size="sm"
                                value={mappedTo != null ? String(mappedTo) : ''}
                                onChange={(_, v) =>
                                  setStatusMap(
                                    cs.value,
                                    v ? Number(v) : null,
                                  )
                                }
                                placeholder="(use board default)"
                              >
                                <Option value="">(use board default)</Option>
                                {statuses.map((s) => (
                                  <Option key={s.id} value={String(s.id)}>
                                    {s.name}
                                  </Option>
                                ))}
                              </Select>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </Sheet>
              )}
              <Typography level="body-xs" sx={{ mt: 1, color: 'neutral.500' }}>
                {boardHeader ? (
                  <>
                    Statuses are matched to the <strong>destination</strong> board
                    per row. For rows routed by the board mapping above, these
                    IDs may not apply — the import falls back to matching by
                    status name on the destination board.
                  </>
                ) : (
                  <>Unmapped values will use the board's default status and surface as a row-level warning.</>
                )}
              </Typography>
            </FormControl>
          )}
        </>
      )}

      {!canProceed && (
        <Alert color="warning" variant="soft" startDecorator={<Warning />}>
          Pick a board before continuing.
        </Alert>
      )}

      <Stack direction="row" justifyContent="space-between">
        <Button variant="plain" onClick={onBack}>
          Back
        </Button>
        <Button onClick={onNext} disabled={!canProceed}>
          Next: Dry Run
        </Button>
      </Stack>
    </Stack>
  );
};

// --------------------------------------------------------------------------
// Step 3: Dry Run
// --------------------------------------------------------------------------
const DryRunStep: React.FC<{
  dataImport: DataImport;
  onRun: () => void;
  onBack: () => void;
  onExecute: () => void;
}> = ({ dataImport, onRun, onBack, onExecute }) => {
  const running = dataImport.status === 'dry_run';
  const done = dataImport.status === 'dry_run_complete';
  const summary = done ? dataImport.dry_run_summary : null;

  const totals = useMemo(() => {
    if (!summary || !('create_count' in summary)) return null;
    return summary;
  }, [summary]);

  // If the import says it's complete but the summary block isn't on hand
  // (e.g. we only got the WS counter payload), pull the full record once so
  // the user sees their totals.
  const [, setNonce] = useState(0);
  useEffect(() => {
    if (done && !totals) {
      importsApi
        .getImport(dataImport.id)
        .then((latest) => {
          // Mutate the live dataImport in place; parent will also see update
          // on next render cycle. Triggering a local rerender guarantees UI
          // refresh if the parent hasn't re-passed the prop.
          (dataImport as DataImport).dry_run_summary = latest.dry_run_summary;
          setNonce((n) => n + 1);
        })
        .catch(() => {
          /* swallow */
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done, totals, dataImport.id]);

  return (
    <Stack spacing={2}>
      <Alert color="neutral" variant="soft">
        A dry run simulates the import using your mapping and options — no records are created or updated.
      </Alert>

      {!done && (
        <Stack direction="row" spacing={1}>
          <Button startDecorator={<Science />} onClick={onRun} disabled={running}>
            {running ? 'Running…' : 'Run Dry Run'}
          </Button>
        </Stack>
      )}

      {running && (
        <Box>
          <LinearProgress
            determinate
            value={dataImport.total_rows ? dataImport.progress_percent : 0}
          />
          <Typography level="body-sm" sx={{ mt: 1 }}>
            {dataImport.processed_rows} / {dataImport.total_rows} rows
          </Typography>
        </Box>
      )}

      {done && totals && (
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip color="success" size="lg" startDecorator={<CheckCircle />}>
              {totals.create_count} to create
            </Chip>
            <Chip color="primary" size="lg">
              {totals.update_count} to update
            </Chip>
            <Chip color="neutral" size="lg">
              {totals.skip_count} to skip
            </Chip>
            <Chip color="warning" size="lg" startDecorator={<Warning />}>
              {totals.warning_count} warnings
            </Chip>
            <Chip color="danger" size="lg" startDecorator={<ErrorIcon />}>
              {totals.error_count} errors
            </Chip>
          </Stack>

          {totals.sample_issues && totals.sample_issues.length > 0 && (
            <Sheet variant="outlined" sx={{ borderRadius: 'md', maxHeight: 240, overflow: 'auto' }}>
              <Table size="sm" stickyHeader>
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Severity</th>
                    <th>Field</th>
                    <th>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {totals.sample_issues.map((issue, idx) => (
                    <tr key={idx}>
                      <td>{issue.row_number}</td>
                      <td>
                        <Chip size="sm" color={issue.severity === 'error' ? 'danger' : 'warning'}>
                          {issue.severity}
                        </Chip>
                      </td>
                      <td>{issue.field ? importFieldLabel(issue.field, issue.entity) : '—'}</td>
                      <td>{issue.message}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Sheet>
          )}
        </Stack>
      )}

      <Stack direction="row" justifyContent="space-between">
        <Button variant="plain" onClick={onBack}>
          Back
        </Button>
        <Stack direction="row" spacing={1}>
          {done && (
            <Button variant="outlined" color="primary" onClick={onRun} startDecorator={<Refresh />}>
              Re-run Dry Run
            </Button>
          )}
          <Button
            color="success"
            startDecorator={<PlayArrow />}
            disabled={!done || totals?.error_count === dataImport.total_rows}
            onClick={onExecute}
          >
            Run Import
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};

// --------------------------------------------------------------------------
// Step 4: Progress
// --------------------------------------------------------------------------
const ProgressStep: React.FC<{
  dataImport: DataImport;
  onNext: () => void;
}> = ({ dataImport, onNext }) => {
  const done = dataImport.status === 'completed' || dataImport.status === 'failed';
  useEffect(() => {
    if (done) {
      const t = window.setTimeout(onNext, 600);
      return () => window.clearTimeout(t);
    }
  }, [done, onNext]);

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Typography level="body-md">
        {dataImport.status === 'completed'
          ? 'Import complete.'
          : dataImport.status === 'failed'
          ? 'Import failed.'
          : `Importing ${dataImport.processed_rows} / ${dataImport.total_rows} rows…`}
      </Typography>

      <LinearProgress
        determinate
        value={dataImport.total_rows ? dataImport.progress_percent : 0}
      />

      <Stack direction="row" spacing={1} flexWrap="wrap">
        <Chip color="success">{dataImport.create_count} created</Chip>
        <Chip color="primary">{dataImport.update_count} updated</Chip>
        <Chip color="neutral">{dataImport.skip_count} skipped</Chip>
        <Chip color="warning">{dataImport.warning_count} warnings</Chip>
        <Chip color="danger">{dataImport.error_count} errors</Chip>
      </Stack>
    </Stack>
  );
};

// --------------------------------------------------------------------------
// Step 5: Results
// --------------------------------------------------------------------------
const ResultsStep: React.FC<{
  dataImport: DataImport;
  onDone: () => void;
}> = ({ dataImport, onDone }) => {
  const failed = dataImport.status === 'failed';
  const hasIssues = dataImport.error_count > 0 || dataImport.warning_count > 0;

  return (
    <Stack spacing={2}>
      {failed ? (
        <Alert color="danger" variant="soft" startDecorator={<ErrorIcon />}>
          {dataImport.error_message || 'Import failed before completing.'}
        </Alert>
      ) : (
        <Alert color="success" variant="soft" startDecorator={<CheckCircle />}>
          {dataImport.status === 'reversed' ? 'Import rewound. Counts below describe the original import.' : 'Import completed.'}
        </Alert>
      )}

      <Stack direction="row" spacing={1} flexWrap="wrap">
        <Chip color="success" size="lg">
          {dataImport.create_count} created
        </Chip>
        <Chip color="primary" size="lg">
          {dataImport.update_count} updated
        </Chip>
        <Chip color="neutral" size="lg">
          {dataImport.skip_count} skipped
        </Chip>
        <Chip color="warning" size="lg">
          {dataImport.warning_count} warnings
        </Chip>
        <Chip color="danger" size="lg">
          {dataImport.error_count} errors
        </Chip>
      </Stack>

      {hasIssues && (
        <Button
          variant="outlined"
          color="primary"
          component="a"
          href={importsApi.errorsCsvUrl(dataImport.id)}
          startDecorator={<Download />}
        >
          Download issues CSV
        </Button>
      )}

      <Divider />

      <Stack direction="row" justifyContent="flex-end">
        <Button onClick={onDone} startDecorator={<InsertDriveFile />}>
          Done
        </Button>
      </Stack>
    </Stack>
  );
};

export default DataImportWizard;
